import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Percent, Send } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@utils/cn';
import { AutoSaveIndicator, AutoSaveState } from '@components/molecules/AutoSaveIndicator';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import { InvoiceBudgetItemPicker } from '@components/organisms/InvoiceBudgetItemPicker';
import {
  InvoiceLineDiscountModal,
  InvoiceLineDiscountTarget,
} from '@components/organisms/InvoiceLineDiscountModal';
import { LineItemFormModal } from '@components/organisms/LineItemFormModal';
import {
  AVAILABLE_BUDGET_ITEMS_QUERY_KEY,
  useGetAvailableBudgetItems,
  useGetInvoice,
  useUpdateInvoiceDraft,
} from '@hooks/api/invoice';
import { useCreateLineItem } from '@hooks/api/lineItem/useCreateLineItem';
import { useUpdateLineItem } from '@hooks/api/lineItem/useUpdateLineItem';
import { useDeleteLineItem } from '@hooks/api/lineItem/useDeleteLineItem';
import { useGetProjectBudgetSummary } from '@hooks/api/lineItem/useGetProjectBudgetSummary';
import {
  AvailableBudgetItem,
  AvailableBudgetItemsResult,
  Invoice,
  InvoiceLineItem,
  InvoiceLineItemSourceType,
} from '@models/invoice.model';
import { BudgetLineItem } from '@models/lineItem.model';
import { InvoiceLineItemInput } from '@services/invoice';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { Input } from '@components/atoms/Input';
import { InputCurrency } from '@components/atoms/InputCurrency';

const AUTOSAVE_DEBOUNCE_MS = 1500;

const PAYMENT_TERMS_OPTIONS: SelectOption[] = [
  { value: '', label: 'Upon receipt' },
  { value: 15, label: 'Net 15' },
  { value: 30, label: 'Net 30' },
  { value: 60, label: 'Net 60' },
];

interface ActiveDraftViewProps {
  invoiceId: string;
  projectId: string;
  onBack: () => void;
  onReviewAndSend: (invoice: Invoice) => void;
  onDelete: () => void;
  isDeleting?: boolean;
}

const formatCurrency = (value: number): string => {
  return `$ ${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const lineItemsToInputs = (lineItems: InvoiceLineItem[]): InvoiceLineItemInput[] =>
  lineItems.map((item, idx) => ({
    description: item.description,
    quantity: Number(item.quantity),
    unitOfMeasure: item.unitOfMeasure,
    unitPrice: Number(item.unitPrice),
    taxAmount: Number(item.taxAmount || 0),
    discountAmount: Number(item.discountAmount || 0),
    displayOrder: idx,
    sourceType: item.sourceType,
    sourceEntityId: item.sourceEntityId,
    taskId: item.taskId ?? null,
    taskName: item.taskName ?? null,
  }));

const availableItemToLineItem = (item: AvailableBudgetItem, idx: number): InvoiceLineItem => {
  // The picker DTO already carries the resolved per-line discount (computed server-side
  // off the source line item). Promoting the row to a cart entry just carries it through
  // — the backend's replaceLineItems re-derives the same lineTotal on save.
  const gross = Number((item.quantity * item.unitPrice).toFixed(2));
  const discountAmount = Number(item.discountAmount ?? 0);
  return {
    invoiceLineItemId: `picker-${item.sourceType}-${item.sourceEntityId}`,
    description: item.description,
    quantity: item.quantity,
    unitOfMeasure: item.unitOfMeasure,
    unitPrice: item.unitPrice,
    lineTotal: Number((gross - discountAmount).toFixed(2)),
    taxAmount: 0,
    discountAmount,
    displayOrder: idx,
    sourceType: item.sourceType,
    sourceEntityId: item.sourceEntityId,
    taskId: item.taskId ?? null,
    taskName: item.taskName ?? null,
  };
};

const computePreSelectedIds = (available?: AvailableBudgetItemsResult | null): Set<string> => {
  if (!available) return new Set();
  const all = [
    ...(available.materials ?? []),
    ...(available.equipment ?? []),
    ...(available.delegatedTaskCosts ?? []),
    ...(available.expenses ?? []),
  ];
  return new Set(
    all
      .filter((item) => item.chargeToClient && item.isAvailableForInvoice)
      .map((item) => item.sourceEntityId),
  );
};

export const ActiveDraftView: React.FC<ActiveDraftViewProps> = ({
  invoiceId,
  projectId,
  onBack,
  onReviewAndSend,
  onDelete,
  isDeleting = false,
}) => {
  const { data: invoice } = useGetInvoice(invoiceId);
  const { data: availableItems, isLoading: pickerLoading } = useGetAvailableBudgetItems(projectId);
  // Project budget summary gives us the full BudgetLineItem (with salePricePerUnit)
  // that the available-items endpoint flattens away. We need that to populate the
  // edit modal with the correct fields without re-querying per-row.
  const { data: budgetSummary } = useGetProjectBudgetSummary(projectId);
  const updateDraft = useUpdateInvoiceDraft();
  const createLineItem = useCreateLineItem();
  const updateLineItemMutation = useUpdateLineItem();
  const deleteLineItemMutation = useDeleteLineItem();
  const queryClient = useQueryClient();

  const [recipientEmail, setRecipientEmail] = useState('');
  const [paymentTerms, setPaymentTerms] = useState<number | undefined>();
  const [poNumber, setPoNumber] = useState('');
  const [taxRate, setTaxRate] = useState(0);
  const [discountRate, setDiscountRate] = useState(0);
  const [discountFixedAmount, setDiscountFixedAmount] = useState(0);
  const [lineItems, setLineItems] = useState<InvoiceLineItem[]>([]);

  const [isLineItemFormOpen, setIsLineItemFormOpen] = useState(false);
  const [editingLineItem, setEditingLineItem] = useState<BudgetLineItem | null>(null);
  const [deletingLineItem, setDeletingLineItem] = useState<{
    lineItemId: string;
    description: string;
  } | null>(null);
  const [discountTarget, setDiscountTarget] = useState<InvoiceLineDiscountTarget | null>(null);

  const [autoSaveState, setAutoSaveState] = useState<AutoSaveState>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [autoSaveError, setAutoSaveError] = useState<string | undefined>();

  const initializedRef = useRef(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedFingerprintRef = useRef<string>('');

  const buildFingerprint = useCallback(
    (data: {
      recipientEmail: string;
      paymentTerms?: number;
      poNumber: string;
      taxRate: number;
      discountRate: number;
      discountFixedAmount: number;
      lineItems: InvoiceLineItem[];
    }): string =>
      JSON.stringify({
        recipientEmail: data.recipientEmail,
        paymentTerms: data.paymentTerms ?? null,
        poNumber: data.poNumber,
        taxRate: Number(data.taxRate),
        discountRate: Number(data.discountRate),
        discountFixedAmount: Number(data.discountFixedAmount),
        lineItems: data.lineItems.map((item) => ({
          description: item.description,
          quantity: Number(item.quantity),
          unitOfMeasure: item.unitOfMeasure,
          unitPrice: Number(item.unitPrice),
          taxAmount: Number(item.taxAmount || 0),
          discountAmount: Number(item.discountAmount || 0),
          sourceType: item.sourceType ?? null,
          sourceEntityId: item.sourceEntityId ?? null,
        })),
      }),
    [],
  );

  useEffect(() => {
    if (initializedRef.current || !invoice) return;
    const initialEmail = invoice.recipientEmail || '';
    const initialPaymentTerms = invoice.paymentTerms ?? undefined;
    const initialPoNumber = invoice.poNumber || '';
    const initialTaxRate = Number(invoice.taxRate || 0);
    const initialDiscountRate = Number(invoice.discountRate || 0);
    const initialDiscountFixed = Number(invoice.discountFixedAmount || 0);
    const initialLineItems = invoice.lineItems ?? [];

    setRecipientEmail(initialEmail);
    setPaymentTerms(initialPaymentTerms);
    setPoNumber(initialPoNumber);
    setTaxRate(initialTaxRate);
    setDiscountRate(initialDiscountRate);
    setDiscountFixedAmount(initialDiscountFixed);
    setLineItems(initialLineItems);

    // Seed the fingerprint with the loaded state so autosave doesn't fire on initial render.
    lastSavedFingerprintRef.current = buildFingerprint({
      recipientEmail: initialEmail,
      paymentTerms: initialPaymentTerms,
      poNumber: initialPoNumber,
      taxRate: initialTaxRate,
      discountRate: initialDiscountRate,
      discountFixedAmount: initialDiscountFixed,
      lineItems: initialLineItems,
    });
    initializedRef.current = true;
  }, [invoice, buildFingerprint]);

  const preSelectedSourceIds = useMemo(
    () => computePreSelectedIds(availableItems),
    [availableItems],
  );

  // Apply pre-selection once availableItems and the draft are both loaded AND the draft is empty.
  useEffect(() => {
    if (!initializedRef.current || !availableItems) return;
    if (lineItems.length > 0) return;
    const presets: InvoiceLineItem[] = [];
    let idx = 0;
    preSelectedSourceIds.forEach((sourceId) => {
      const all = [
        ...(availableItems.materials ?? []),
        ...(availableItems.equipment ?? []),
        ...(availableItems.delegatedTaskCosts ?? []),
        ...(availableItems.expenses ?? []),
      ];
      const found = all.find((row) => row.sourceEntityId === sourceId);
      if (found) presets.push(availableItemToLineItem(found, idx++));
    });
    if (presets.length > 0) setLineItems(presets);
  }, [availableItems, preSelectedSourceIds, lineItems.length]);

  const selectedSourceIds = useMemo(() => {
    return new Set(
      lineItems.filter((item) => item.sourceEntityId).map((item) => item.sourceEntityId as string),
    );
  }, [lineItems]);

  // Map sourceEntityId → cart discount amount. The picker reads this to render
  // the discount badge on MATERIAL / EQUIPMENT rows whose discount is per-invoice.
  const cartDiscountBySourceId = useMemo(() => {
    const map = new Map<string, number>();
    for (const li of lineItems) {
      if (!li.sourceEntityId) continue;
      const amount = Number(li.discountAmount || 0);
      if (amount > 0) map.set(li.sourceEntityId, amount);
    }
    return map;
  }, [lineItems]);

  const linkedLineItems = useMemo(() => lineItems.filter((item) => item.sourceEntityId), [lineItems]);
  const manualLineItems = useMemo(() => lineItems.filter((item) => !item.sourceEntityId), [lineItems]);

  const subtotal = useMemo(
    () => Number(lineItems.reduce((sum, item) => sum + Number(item.lineTotal || 0), 0).toFixed(2)),
    [lineItems],
  );
  const discountAmount = useMemo(() => {
    const raw = (subtotal * discountRate) / 100 + Number(discountFixedAmount || 0);
    return Number(Math.min(raw, subtotal).toFixed(2));
  }, [subtotal, discountRate, discountFixedAmount]);
  const taxAmount = useMemo(
    () => Number((((subtotal - discountAmount) * taxRate) / 100).toFixed(2)),
    [subtotal, discountAmount, taxRate],
  );
  const total = useMemo(
    () => Number((subtotal - discountAmount + taxAmount).toFixed(2)),
    [subtotal, discountAmount, taxAmount],
  );
  const preSelectedCount = useMemo(
    () => linkedLineItems.filter((item) => preSelectedSourceIds.has(item.sourceEntityId as string)).length,
    [linkedLineItems, preSelectedSourceIds],
  );

  const currentFingerprint = useMemo(
    () =>
      buildFingerprint({
        recipientEmail,
        paymentTerms,
        poNumber,
        taxRate,
        discountRate,
        discountFixedAmount,
        lineItems,
      }),
    [
      buildFingerprint,
      recipientEmail,
      paymentTerms,
      poNumber,
      taxRate,
      discountRate,
      discountFixedAmount,
      lineItems,
    ],
  );

  const persistDraft = useCallback(async (): Promise<Invoice | null> => {
    if (!invoice) return null;
    const fingerprintAtSaveStart = currentFingerprint;
    setAutoSaveState('saving');
    setAutoSaveError(undefined);
    try {
      const updated = await updateDraft.mutateAsync({
        invoiceId,
        expectedVersion: invoice.version,
        recipientEmail,
        taxRate,
        discountRate,
        discountFixedAmount,
        poNumber: poNumber || undefined,
        paymentTerms,
        lineItems: lineItemsToInputs(lineItems),
      });
      setAutoSaveState('saved');
      setLastSavedAt(new Date());
      lastSavedFingerprintRef.current = fingerprintAtSaveStart;
      return updated;
    } catch (error: any) {
      const message =
        error?.response?.errors?.[0]?.message || error?.message || 'Failed to save changes';
      setAutoSaveError(message);
      setAutoSaveState('error');
      return null;
    }
  }, [invoice, invoiceId, lineItems, paymentTerms, poNumber, recipientEmail, taxRate, discountRate, discountFixedAmount, updateDraft, currentFingerprint]);

  useEffect(() => {
    if (!initializedRef.current) return;
    // Only schedule a save when the current state actually differs from the last saved snapshot.
    if (currentFingerprint === lastSavedFingerprintRef.current) return;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      persistDraft();
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [currentFingerprint, persistDraft]);

  const handleToggleSelection = (item: AvailableBudgetItem) => {
    setLineItems((prev) => {
      const existingIdx = prev.findIndex(
        (li) => li.sourceEntityId === item.sourceEntityId && li.sourceType === item.sourceType,
      );
      if (existingIdx >= 0) {
        const next = prev.slice();
        next.splice(existingIdx, 1);
        return next;
      }
      return [...prev, availableItemToLineItem(item, prev.length)];
    });
  };

  const handleSelectAllAvailable = () => {
    if (!availableItems) return;
    const all = [
      ...(availableItems.materials ?? []),
      ...(availableItems.equipment ?? []),
      ...(availableItems.delegatedTaskCosts ?? []),
      ...(availableItems.expenses ?? []),
    ].filter((row) => row.isAvailableForInvoice);
    const existing = new Map(
      lineItems
        .filter((li) => li.sourceEntityId)
        .map((li) => [li.sourceEntityId as string, li]),
    );
    let idx = lineItems.length;
    const additions: InvoiceLineItem[] = [];
    all.forEach((row) => {
      if (!existing.has(row.sourceEntityId)) {
        additions.push(availableItemToLineItem(row, idx++));
      }
    });
    if (additions.length > 0) setLineItems([...lineItems, ...additions]);
  };

  const handleResetToDefaults = () => {
    if (!availableItems) return;
    const linkedReset: InvoiceLineItem[] = [];
    let idx = 0;
    preSelectedSourceIds.forEach((sourceId) => {
      const all = [
        ...(availableItems.materials ?? []),
        ...(availableItems.equipment ?? []),
        ...(availableItems.delegatedTaskCosts ?? []),
        ...(availableItems.expenses ?? []),
      ];
      const found = all.find((row) => row.sourceEntityId === sourceId);
      if (found) linkedReset.push(availableItemToLineItem(found, idx++));
    });
    setLineItems([...linkedReset, ...manualLineItems]);
  };

  const handleCreateLineItemFromModal = (values: {
    description: string;
    quantity: number;
    unitOfMeasure: string;
    amountSystem: string;
    unitPrice: number;
    salePricePerUnit: number | null;
    chargeToClient: boolean;
    discountType: 'USD' | 'PERCENT' | null;
    discountValue: number;
    notes: string;
  }) => {
    createLineItem.mutate(
      {
        projectId,
        description: values.description,
        quantity: values.quantity,
        unitOfMeasure: values.unitOfMeasure,
        amountSystem: values.amountSystem,
        unitPrice: values.unitPrice,
        // In the invoice context the user is by definition charging this to the client.
        // Default salePricePerUnit to unitPrice if not provided (no markup) — same convention
        // we use for non-delegated items in the budget flow.
        salePricePerUnit: values.salePricePerUnit ?? values.unitPrice,
        chargeToClient: true,
        discountType: values.discountType,
        discountValue: values.discountValue,
        notes: values.notes,
      },
      {
        onSuccess: (created: any) => {
          // Bill off the sale price the client pays — never the internal cost.
          // Mirrors handleUpdateLineItemFromModal and the backend's
          // getAvailableBudgetItems (which surfaces `salePricePerUnit ?? unitPrice`).
          const unitPrice = Number(
            created?.salePricePerUnit ?? created?.unitPrice ?? values.salePricePerUnit ?? values.unitPrice,
          );
          const quantity = Number(created?.quantity ?? values.quantity);
          // Compute the per-line discount from the just-created line item so the picker
          // row matches the cart row immediately (no wait for the availableItems refetch).
          const dType = (created?.discountType ?? values.discountType) as
            | 'USD'
            | 'PERCENT'
            | null;
          const dVal = Number(created?.discountValue ?? values.discountValue ?? 0);
          const perUnitDiscount =
            dType && dVal > 0
              ? dType === 'USD'
                ? dVal
                : (unitPrice * dVal) / 100
              : 0;
          const grossTotal = Number((quantity * unitPrice).toFixed(2));
          const discountAmount = Math.min(
            grossTotal,
            Number((perUnitDiscount * quantity).toFixed(2)),
          );
          const newItem: InvoiceLineItem = {
            invoiceLineItemId: `picker-LINE_ITEM-${created.lineItemId}`,
            description: created.description,
            quantity,
            unitOfMeasure: created?.unitOfMeasure || values.unitOfMeasure || 'EACH',
            unitPrice,
            lineTotal: Number((grossTotal - discountAmount).toFixed(2)),
            taxAmount: 0,
            discountAmount,
            displayOrder: lineItems.length,
            sourceType: InvoiceLineItemSourceType.LINE_ITEM,
            sourceEntityId: created.lineItemId,
          };
          setLineItems([...lineItems, newItem]);
          queryClient.invalidateQueries({
            queryKey: [AVAILABLE_BUDGET_ITEMS_QUERY_KEY],
            refetchType: 'active',
          });
          setIsLineItemFormOpen(false);
        },
      },
    );
  };

  const handleEditLineItem = (item: AvailableBudgetItem) => {
    // Look up the full BudgetLineItem from the budget summary so the form sees
    // the real salePricePerUnit / chargeToClient values, not the flattened picker view.
    const full = budgetSummary?.lineItems?.find(
      (li: BudgetLineItem) => li.lineItemId === item.sourceEntityId,
    );
    if (full) {
      setEditingLineItem(full);
    } else {
      // Fallback: synthesize from the picker row. Edits will still work; salePricePerUnit
      // simply re-initializes from unitPrice (the value the picker shows for line items).
      setEditingLineItem({
        lineItemId: item.sourceEntityId,
        description: item.description,
        quantity: item.quantity,
        unitOfMeasure: item.unitOfMeasure,
        amountSystem: 'imperial',
        unitPrice: item.unitPrice,
        salePricePerUnit: null,
        cost: item.total,
        salePrice: null,
        chargeToClient: item.chargeToClient,
        isOwnItem: true,
        organizationName: null,
      });
    }
  };

  const handleUpdateLineItemFromModal = (values: {
    description: string;
    quantity: number;
    unitOfMeasure: string;
    amountSystem: string;
    unitPrice: number;
    salePricePerUnit: number | null;
    chargeToClient: boolean;
    discountType: 'USD' | 'PERCENT' | null;
    discountValue: number;
    notes: string;
  }) => {
    if (!editingLineItem) return;
    const lineItemId = editingLineItem.lineItemId;
    updateLineItemMutation.mutate(
      {
        lineItemId,
        description: values.description,
        quantity: values.quantity,
        unitOfMeasure: values.unitOfMeasure,
        amountSystem: values.amountSystem,
        unitPrice: values.unitPrice,
        // In the invoice context we keep chargeToClient=true so the item stays available
        // to bill. If the user explicitly set a sale price, honor it; otherwise fall back
        // to unitPrice so the cart shows the right number.
        salePricePerUnit: values.salePricePerUnit ?? values.unitPrice,
        chargeToClient: true,
        discountType: values.discountType,
        discountValue: values.discountValue,
        notes: values.notes,
      },
      {
        onSuccess: (updated: any) => {
          // If the edited item is currently in the cart, re-sync its display fields.
          const newSale = Number(updated?.salePricePerUnit ?? updated?.unitPrice ?? values.unitPrice);
          const newQty = Number(updated?.quantity ?? values.quantity);
          const newUom = updated?.unitOfMeasure || values.unitOfMeasure || 'EACH';
          const newDescription = updated?.description ?? values.description;
          const newDType = (updated?.discountType ?? values.discountType) as
            | 'USD'
            | 'PERCENT'
            | null;
          const newDVal = Number(updated?.discountValue ?? values.discountValue ?? 0);
          const perUnitDiscount =
            newDType && newDVal > 0
              ? newDType === 'USD'
                ? newDVal
                : (newSale * newDVal) / 100
              : 0;
          const newGross = Number((newQty * newSale).toFixed(2));
          const newDiscountAmount = Math.min(
            newGross,
            Number((perUnitDiscount * newQty).toFixed(2)),
          );
          setLineItems((prev) =>
            prev.map((li) =>
              li.sourceType === InvoiceLineItemSourceType.LINE_ITEM &&
              li.sourceEntityId === lineItemId
                ? {
                    ...li,
                    description: newDescription,
                    quantity: newQty,
                    unitOfMeasure: newUom,
                    unitPrice: newSale,
                    discountAmount: newDiscountAmount,
                    lineTotal: Number((newGross - newDiscountAmount).toFixed(2)),
                  }
                : li,
            ),
          );
          setEditingLineItem(null);
        },
      },
    );
  };

  const handleConfirmDeleteLineItem = () => {
    if (!deletingLineItem) return;
    const lineItemId = deletingLineItem.lineItemId;
    deleteLineItemMutation.mutate(lineItemId, {
      onSuccess: () => {
        // Drop it from the cart if it was selected.
        setLineItems((prev) =>
          prev.filter(
            (li) =>
              !(
                li.sourceType === InvoiceLineItemSourceType.LINE_ITEM &&
                li.sourceEntityId === lineItemId
              ),
          ),
        );
        setDeletingLineItem(null);
      },
    });
  };

  const openDiscountModal = (item: AvailableBudgetItem) => {
    const cartItem = lineItems.find((li) => li.sourceEntityId === item.sourceEntityId);
    setDiscountTarget({
      sourceEntityId: item.sourceEntityId,
      description: item.description,
      quantity: Number(cartItem?.quantity ?? item.quantity),
      unitPrice: Number(cartItem?.unitPrice ?? item.unitPrice),
      currentDiscountAmount: Number(cartItem?.discountAmount ?? 0),
    });
  };

  const openDiscountModalFromCart = (li: InvoiceLineItem) => {
    setDiscountTarget({
      sourceEntityId: li.sourceEntityId as string,
      description: li.description,
      quantity: Number(li.quantity),
      unitPrice: Number(li.unitPrice),
      currentDiscountAmount: Number(li.discountAmount || 0),
    });
  };

  const applyLineDiscount = (discountAmount: number) => {
    if (!discountTarget) return;
    const targetSource = discountTarget.sourceEntityId;
    setLineItems((prev) =>
      prev.map((li) => {
        if (li.sourceEntityId !== targetSource) return li;
        const gross = Number((Number(li.quantity) * Number(li.unitPrice)).toFixed(2));
        const clamped = Math.min(gross, Math.max(0, Number(discountAmount)));
        return {
          ...li,
          discountAmount: clamped,
          lineTotal: Number((gross - clamped).toFixed(2)),
        };
      }),
    );
    setDiscountTarget(null);
  };

  const removeLineDiscount = () => {
    if (!discountTarget) return;
    applyLineDiscount(0);
  };

  const handleReview = async () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    // Use the freshly saved invoice (with bumped version) when available so the preview
    // drawer's subsequent saves don't 409 against a stale version we never refreshed.
    const persisted = await persistDraft();
    const base = persisted ?? invoice;
    if (base) {
      const merged: Invoice = {
        ...base,
        recipientEmail,
        paymentTerms,
        poNumber,
        taxRate,
        tax: taxAmount,
        discountRate,
        discountFixedAmount,
        discountAmount,
        lineItems,
        subtotal,
        total,
      };
      onReviewAndSend(merged);
    }
  };

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <button type="button" onClick={onBack} className="text-gray-400 hover:text-gray-600">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <p className="text-xs text-gray-500">
              Drafts · {invoice?.orderDescription || `Draft ${invoiceId.slice(0, 8)}`}
            </p>
            <h3 className="text-sm font-semibold text-gray-900">Draft invoice</h3>
          </div>
        </div>
        <AutoSaveIndicator state={autoSaveState} lastSavedAt={lastSavedAt} errorMessage={autoSaveError} />
      </div>

      <div className="px-6 py-3 bg-gray-50 border-b border-gray-100 grid grid-cols-3 gap-4">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-wide text-gray-500">Recipient email</span>
          <Input
            type="email"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-wide text-gray-500">Payment terms</span>
          <ReactSelect
            isClearable={false}
            isSearchable={false}
            options={PAYMENT_TERMS_OPTIONS}
            value={
              PAYMENT_TERMS_OPTIONS.find(
                (option) => option.value === (paymentTerms ?? ''),
              ) ?? PAYMENT_TERMS_OPTIONS[0]
            }
            onChange={(selected) => {
              const value = (selected as SelectOption | null)?.value;
              setPaymentTerms(
                value === '' || value === undefined ? undefined : Number(value),
              );
            }}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-wide text-gray-500">PO # (optional)</span>
          <Input
            type="text"
            value={poNumber}
            onChange={(e) => setPoNumber(e.target.value)}
            placeholder="—"
          />
        </label>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 overflow-y-auto p-4">
          <InvoiceBudgetItemPicker
            data={availableItems}
            selectedSourceIds={selectedSourceIds}
            preSelectedSourceIds={preSelectedSourceIds}
            onToggleSelection={handleToggleSelection}
            onSelectAllAvailable={handleSelectAllAvailable}
            onResetToDefaults={handleResetToDefaults}
            onEditLineItem={handleEditLineItem}
            onDeleteLineItem={(item) =>
              setDeletingLineItem({
                lineItemId: item.sourceEntityId,
                description: item.description,
              })
            }
            onAddLineItem={() => setIsLineItemFormOpen(true)}
            isAddingLineItem={createLineItem.isPending}
            onEditDiscount={openDiscountModal}
            cartDiscountBySourceId={cartDiscountBySourceId}
            loading={pickerLoading}
          />
        </div>

        <aside className="w-80 border-l border-gray-200 bg-gray-50 flex flex-col overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200">
            <p className="text-xs uppercase tracking-wide text-gray-500">This invoice</p>
            <p className="text-base font-semibold text-gray-900 mt-1">
              {lineItems.length} item{lineItems.length === 1 ? '' : 's'} selected
            </p>
            <p className="text-xs text-gray-500">
              {preSelectedCount} pre-selected
              {manualLineItems.length > 0 && ` · ${manualLineItems.length} legacy`}
            </p>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-2 text-xs space-y-2">
            {lineItems.length === 0 && (
              <p className="text-gray-400 italic">No items selected yet.</p>
            )}
            {lineItems.map((item) => {
              const lineDiscount = Number(item.discountAmount || 0);
              const gross = Number(
                (Number(item.quantity) * Number(item.unitPrice)).toFixed(2),
              );
              const lineTotal = Number(item.lineTotal || 0);
              const canEditCartDiscount =
                !!item.sourceEntityId &&
                (item.sourceType === InvoiceLineItemSourceType.TASK_MATERIAL ||
                  item.sourceType === InvoiceLineItemSourceType.TASK_EQUIPMENT);
              return (
                <div
                  key={item.invoiceLineItemId}
                  className="flex items-start justify-between gap-2 py-1 border-b border-gray-100 last:border-b-0">
                  <div className="min-w-0 flex-1">
                    <p className="text-gray-800 truncate" title={item.description}>
                      {item.description}
                    </p>
                    {lineDiscount > 0 && (
                      <p className="text-[10px] text-amber-700 mt-0.5">
                        Gross {formatCurrency(gross)} · −{formatCurrency(lineDiscount)} discount
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className="text-gray-700 font-medium">
                      {formatCurrency(lineTotal)}
                    </span>
                    {canEditCartDiscount && (
                      <button
                        type="button"
                        onClick={() => openDiscountModalFromCart(item)}
                        className={cn(
                          'p-0.5 rounded',
                          lineDiscount > 0
                            ? 'text-amber-700 hover:text-amber-800'
                            : 'text-gray-400 hover:text-amber-700',
                        )}
                        aria-label={
                          lineDiscount > 0 ? 'Edit line discount' : 'Add line discount'
                        }
                        title={
                          lineDiscount > 0 ? 'Edit line discount' : 'Add line discount'
                        }>
                        <Percent className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="px-4 py-3 border-t border-gray-200 space-y-1.5 text-xs">
            <div className="flex justify-between text-gray-600">
              <span>Subtotal</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>
            <div className="flex justify-between text-gray-600 items-center">
              <span>Discount (%)</span>
              <div className="flex items-center gap-1.5">
                <div className="w-28">
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    step="0.01"
                    value={discountRate === 0 ? '' : discountRate}
                    onChange={(e) => setDiscountRate(e.target.value === '' ? 0 : Number(e.target.value))}
                    placeholder="0"
                    className="text-right"
                  />
                </div>
                <span className="w-16 text-right text-gray-500" />
              </div>
            </div>
            <div className="flex justify-between text-gray-600 items-center">
              <span>Discount ($)</span>
              <div className="flex items-center gap-1.5">
                <div className="w-36">
                  <InputCurrency
                    value={discountFixedAmount === 0 ? null : discountFixedAmount}
                    onValueChange={(n) => setDiscountFixedAmount(n ?? 0)}
                    placeholder="0"
                  />
                </div>
                <span className="w-16 text-right text-gray-500">
                  {discountAmount > 0 ? '−' : ''}
                  {formatCurrency(discountAmount)}
                </span>
              </div>
            </div>
            <div className="flex justify-between text-gray-600 items-center">
              <span>Tax (%)</span>
              <div className="flex items-center gap-1.5">
                <div className="w-28">
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    step="0.01"
                    value={taxRate === 0 ? '' : taxRate}
                    onChange={(e) => setTaxRate(e.target.value === '' ? 0 : Number(e.target.value))}
                    placeholder="0"
                    className="text-right"
                  />
                </div>
                <span className="w-16 text-right text-gray-500">{formatCurrency(taxAmount)}</span>
              </div>
            </div>
            <div className="flex justify-between font-semibold text-gray-900 text-sm border-t border-gray-200 pt-2 mt-2">
              <span>Total</span>
              <span>{formatCurrency(total)}</span>
            </div>
          </div>

          <div className="px-4 py-3 border-t border-gray-200 space-y-2 bg-white">
            <button
              type="button"
              disabled={lineItems.length === 0 || total <= 0}
              onClick={handleReview}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded text-sm font-medium text-white bg-blue-900 hover:bg-blue-800 disabled:opacity-50">
              <Send className="w-4 h-4" />
              Review & Send
            </button>
            <button
              type="button"
              onClick={onDelete}
              disabled={isDeleting}
              className="w-full text-xs text-gray-500 hover:text-red-600 disabled:opacity-50">
              {isDeleting ? 'Deleting…' : 'Delete this draft'}
            </button>
          </div>
        </aside>
      </div>

      <LineItemFormModal
        isOpen={isLineItemFormOpen}
        onClose={() => setIsLineItemFormOpen(false)}
        lineItem={null}
        onSubmit={handleCreateLineItemFromModal}
      />

      <LineItemFormModal
        isOpen={!!editingLineItem}
        onClose={() => setEditingLineItem(null)}
        lineItem={editingLineItem}
        onSubmit={handleUpdateLineItemFromModal}
      />

      <ConfirmationModal
        isOpen={!!deletingLineItem}
        onClose={() => setDeletingLineItem(null)}
        onConfirm={handleConfirmDeleteLineItem}
        title="Delete line item"
        description={
          deletingLineItem
            ? `Delete "${deletingLineItem.description}"? This will remove it from the project's line items and from this draft.`
            : ''
        }
        confirmText={deleteLineItemMutation.isPending ? 'Deleting…' : 'Delete'}
        variant="danger"
      />

      <InvoiceLineDiscountModal
        isOpen={!!discountTarget}
        onClose={() => setDiscountTarget(null)}
        target={discountTarget}
        onApply={applyLineDiscount}
        onRemove={removeLineDiscount}
      />
    </div>
  );
};
